import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Collapse, Stack, Typography, useTheme } from '@mui/material';

import CareTaskInterventionList from './CareTaskInterventionList';
import PlannedCareCardFormFragment from './PlannedCareCardFormFragment';
import { useAnalytics } from 'src/AnalyticsContext';
import CareTaskDetailDrawer from 'src/components/drawers/CareTaskDetailDrawer';
import TaskingCheckbox from 'src/components/fragments/TaskingCheckbox';
import { ResolutionTypesById } from 'src/constants/ResolutionTypes';
import { ResidentCareFormValues } from 'src/hooks/forms/useResidentCareTaskForm';
import { CareTask } from 'src/store/sageAdminApi';
import { pluralize } from 'src/utils/intlUtils';
import {
    ChartType,
    createFormIdFromCareTask,
    generateDefaultFormValues,
} from 'src/utils/residentCareFormUtils';

interface PlannedCareCardProps {
    careTask: CareTask;
    showIcon?: boolean;
    showIncompleteButton?: boolean;
    shouldRestrictTimeFields?: boolean;
}

// This expects a FromProvider with useResidentCareForm
// State is maintained by the form. Field changes are sent directly to the form
const PlannedCareCardFormContainer = ({
    showIcon = true,
    showIncompleteButton = true,
    shouldRestrictTimeFields = false,
    careTask,
}: PlannedCareCardProps) => {
    const { control, setValue, unregister } = useFormContext<ResidentCareFormValues>();
    const { palette } = useTheme();
    const [showDetailDrawer, setShowDetailDrawer] = useState(false);

    const { trackEvent } = useAnalytics();

    const taskFormId = useMemo(() => createFormIdFromCareTask(careTask), [careTask]);

    const chartType = useWatch({
        control,
        name: `plannedCare.${taskFormId}.chartType`,
    });

    const categoryIcon = ResolutionTypesById[careTask.careTaskCategory]?.icon;
    const interventions = careTask.interventions ?? [];

    const taskDetail = interventions.length
        ? {
              linkLabel: pluralize(interventions.length, 'intervention', 'interventions'),
              heading: 'Interventions',
              body: <CareTaskInterventionList interventions={interventions} />,
          }
        : careTask.instructions
        ? {
              linkLabel: 'Instructions',
              heading: 'Instructions',
              body: <Typography variant="body2">{careTask.instructions}</Typography>,
          }
        : undefined;
    const formattedScheduledAtTime = careTask.scheduledAtMs
        ? format(careTask.scheduledAtMs, 'h:mmaaa')
        : undefined;
    const [isExpanded, setIsExpanded] = useState(() => !!chartType);

    // Collapse the card when the form is externally cleared (e.g. "Clear All" reset)
    useEffect(() => {
        if (!chartType) setIsExpanded(false);
    }, [chartType]);

    // We do this just so that when the form is reset ("Clear All"), we keep showing a good UI while the form collapses in.
    const lastChartType = useRef<ChartType>('complete');
    if (chartType) {
        lastChartType.current = chartType;
    }

    const addPlannedCareToForm = (chartType: ChartType) => {
        const { defaultDurationInMinutes, defaultStartTime, defaultEndTime } =
            generateDefaultFormValues(careTask, chartType, shouldRestrictTimeFields);

        trackEvent('Care Task Form Opened', {
            careTaskCategory: careTask.careTaskCategory,
            chartType,
        });

        setValue(
            `plannedCare.${taskFormId}`,
            {
                chartType,
                careTaskRid: careTask.id,
                careTaskCategory: careTask.careTaskCategory,
                // Incomplete chart types require selecting an outcome
                outcome: chartType === 'complete' ? 'COMPLETED' : undefined,
                durationInMinutes: defaultDurationInMinutes,
                startTime: defaultStartTime,
                endTime: defaultEndTime,
            },
            { shouldDirty: true }
        );

        setIsExpanded(true);
    };

    const removePlannedCareFromForm = () => {
        unregister(`plannedCare.${taskFormId}`);
    };

    const setPlannedCareChartType = (chartType: ChartType) => {
        // Switching the chartType should reset the form fields
        removePlannedCareFromForm();
        addPlannedCareToForm(chartType);
    };

    const handleSetChartType = (newChartType: ChartType) => {
        // If there is no existing form, create one
        if (!chartType) {
            trackEvent('Outcome Selection', {
                pageName: 'Log Care',
                residentId: careTask.residentId,
                careTaskId: careTask.id,
                fieldSelected: newChartType,
            });
            addPlannedCareToForm(newChartType);
        } else {
            // User clicked the same button to de-select
            if (newChartType === chartType) {
                setIsExpanded(false);
                trackEvent('Care Task Form Abandoned', {
                    careTaskCategory: careTask.careTaskCategory,
                    chartType,
                });
            } else {
                trackEvent('Outcome Selection', {
                    pageName: 'Log Care',
                    residentId: careTask.residentId,
                    careTaskId: careTask.id,
                    fieldSelected: newChartType,
                });
                setPlannedCareChartType(newChartType);
            }
        }
    };

    useEffect(() => {
        return () => removePlannedCareFromForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const trackCareTaskDrawerEvent = (eventName: string) =>
        trackEvent(eventName, {
            pageName: 'Log Care',
            careTaskCategory: careTask.careTaskCategory,
            careTaskId: careTask.id,
        });

    const handleDetailClick = (e: React.MouseEvent<HTMLSpanElement>, heading: string) => {
        e.stopPropagation();
        trackCareTaskDrawerEvent(`Care Task ${heading} Opened`);
        setShowDetailDrawer(true);
    };

    const handleDetailClose = (heading: string) => {
        trackCareTaskDrawerEvent(`Care Task ${heading} Closed`);
        setShowDetailDrawer(false);
    };

    const outlineStyles = useMemo(() => {
        if (chartType === undefined) {
            return `1px solid ${palette.grey[100]}`;
        }
        return chartType === 'incomplete'
            ? `2px solid ${palette.error.main}`
            : `2px solid ${palette.primary.main}`;
    }, [chartType, palette]);

    return (
        <>
            <Card
                sx={{
                    borderRadius: 3,
                    boxShadow: 0,
                    transition: 'outline-color 0.2s',
                    outline: outlineStyles,
                }}
            >
                <Stack
                    direction="row"
                    onClick={
                        // If there is no incomplete button, there is just the complete one. So let the user click anywhere to complete the task
                        showIncompleteButton ? undefined : () => handleSetChartType('complete')
                    }
                    sx={{ gap: 2, justifyContent: 'space-between', height: '100%', p: 2 }}
                >
                    <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                        {categoryIcon && showIcon && (
                            <Box>
                                <FontAwesomeIcon icon={categoryIcon} size="2x" fixedWidth />
                            </Box>
                        )}
                        <Stack>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}
                            >
                                {careTask.name}
                            </Typography>
                            <Typography variant="body2" sx={{ py: 0.25 }}>
                                {formattedScheduledAtTime}
                                {formattedScheduledAtTime && taskDetail && (
                                    <Box component="span" sx={{ mx: 0.5 }}>
                                        •
                                    </Box>
                                )}
                                {taskDetail && (
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        onClick={(e) => handleDetailClick(e, taskDetail.heading)}
                                        sx={{
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {taskDetail.linkLabel}
                                    </Typography>
                                )}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                        {/* TODO: In the alert flow, the complete button is a checkbox. In the log care page, these are toggle buttons. They should be treated as such */}
                        {showIncompleteButton && (
                            <TaskingCheckbox
                                variant="incomplete"
                                onClick={() => handleSetChartType('incomplete')}
                                checked={chartType === 'incomplete'}
                            />
                        )}
                        <TaskingCheckbox
                            hideUncheckedIcon={!showIncompleteButton}
                            onClick={
                                showIncompleteButton
                                    ? () => handleSetChartType('complete')
                                    : undefined
                            }
                            checked={chartType === 'complete'}
                        />
                    </Stack>
                </Stack>
                <Collapse in={isExpanded} onExited={removePlannedCareFromForm} unmountOnExit>
                    <PlannedCareCardFormFragment
                        taskFormId={taskFormId}
                        careTask={careTask}
                        chartType={lastChartType.current}
                        shouldRestrictTimeFields={shouldRestrictTimeFields}
                        trackingFields={{
                            residentId: careTask.residentId,
                            careTaskId: careTask.id,
                        }}
                    />
                </Collapse>
            </Card>
            {taskDetail && (
                <CareTaskDetailDrawer
                    careTaskName={careTask.name || ''}
                    heading={taskDetail.heading}
                    careTaskIcon={categoryIcon}
                    careTaskScheduledAt={formattedScheduledAtTime}
                    open={showDetailDrawer}
                    onClose={() => handleDetailClose(taskDetail.heading)}
                >
                    {taskDetail.body}
                </CareTaskDetailDrawer>
            )}
        </>
    );
};

export default PlannedCareCardFormContainer;
